import { useEffect } from 'react';
import { DocumentNode, OperationVariables } from '@apollo/client';

import { Requested } from 'modules/ai/types';
import { useLazyGraphql, EnhancedLazyGraphqlResult } from 'utils/hooks/useLazyGraphql';

export interface CampaignsDataQuery<CampaignDataT> {
  campaigns: {
    results: Array<CampaignDataT>;
  };
}

type CampaignResponseResult<CampaignDataT> = Requested<CampaignsDataQuery<CampaignDataT>>;

export const getCampaignsResults = <CampaignDataT>(
  campaignsResponse: CampaignResponseResult<CampaignDataT>,
) => {
  return campaignsResponse?.campaigns?.results || [];
};

export function transformToCampaignsQueryData<CampaignDataT, QueryVariables>(
  queryCampaignsResult: EnhancedLazyGraphqlResult<CampaignDataT, QueryVariables>,
) {
  return {
    ...queryCampaignsResult,
    // @ts-ignore
    data: getCampaignsResults<CampaignDataT>(queryCampaignsResult.data),
    isError: queryCampaignsResult.error,
  };
}

export const parseAsCampaignsVars = (campaignIds: (number | string)[]) => ({
  ids: (campaignIds || []).map(Number),
});

export const useCampaignsDataQuery = <CampaignDataT>(
  campaignsQuery: DocumentNode,
  isDataLoading: boolean,
  isDataLoadingError: boolean,
  ids: number[],
) => {
  const queryResult = useLazyGraphql(campaignsQuery);
  const { fetch } = queryResult;

  useEffect(() => {
    if (isDataLoading) {
      return;
    }

    if (!isDataLoading && !isDataLoadingError) {
      fetch(parseAsCampaignsVars(ids));
    }
  }, [isDataLoading, isDataLoadingError]);

  return transformToCampaignsQueryData<CampaignDataT, OperationVariables>(queryResult);
};
